import { STORAGE_URL } from '@/configs';
import { syncStorageIntoStateService } from '../services';
import { localStorageLabels } from '../constants';
import { version } from '../../../package.json';
import { deviceDetect } from 'react-device-detect';
import { platform } from '@/platform';

export const hasKey = (object, key) => {
  return Object.keys(object).some(function (member) {
    return ~member.indexOf(key);
  });
};

export const getTenantId = () => {
  const tid = syncStorageIntoStateService(localStorageLabels.TID);
  let tenantId = tid ?? '';

  if (tenantId) return tenantId;

  // Only the hosted player can carry the tenant in its subdomain (tenant.player...); a packaged
  // app's host (e.g. appassets.androidplatform.net) is not a tenant.
  if (platform.isPackaged) return '';

  tenantId = window.location.host.split('.')[1]
    ? window.location.host.split('.')[0]
    : '';

  if (
    tenantId === 'dplayer' ||
    tenantId === 'splayer' ||
    tenantId === 'player' ||
    tenantId === 'playerv1' ||
    tenantId === 'v1'
  )
    tenantId = '';

  return tenantId;
};

// Ably channel names, namespaced by tenant. Keep in sync with the backend's RealtimeChannels.
export const realtimeChannels = {
  tenant: (tid) => `tenant:${tid}`,
  screen: (tid, screenId) => `tenant:${tid}:screen:${screenId}`,
  presence: (tid) => `tenant:${tid}:presence`,
  live: (tid, screenId) => `tenant:${tid}:live:${screenId}`,
};

export const getGeneralPath = (tid) => STORAGE_URL + tid + `/general/`;
export const getContentPath = (tid) => STORAGE_URL + tid + `/contents/`;

// What the console shows about this screen's player: browser/device (react-device-detect), the
// platform's own details (playerType, model, firmware, ...) and device storage.
export const screenInfoWithAppVersion = async () => {
  const screenInfo = deviceDetect();
  const osVersion = await realOsVersion();
  const [host, storage] = await Promise.all([
    platform.info().catch(() => ({})),
    platform.media.stats().catch(() => null),
  ]);
  return {
    ...screenInfo,
    // osVersionVerified: the console may trust osVersion (not the frozen UA value).
    ...(osVersion && { osVersion, osVersionVerified: true }),
    // A packaged app knows its OS and model better than the user agent does.
    ...withoutEmpty(host),
    appVersion: version,
    ...(storage?.freeBytes != null && {
      storage: { freeBytes: storage.freeBytes, usedBytes: storage.usedBytes },
    }),
  };
};

const withoutEmpty = (object) =>
  Object.fromEntries(
    Object.entries(object || {}).filter(
      ([, v]) => v !== undefined && v !== null && v !== '',
    ),
  );

// User agents freeze the OS version (Windows 11 reports "Windows NT 10.0", macOS "10.15.7").
// Chromium browsers expose the real one through client hints; elsewhere keep the UA's value.
export const realOsVersion = async (
  userAgentData = globalThis.navigator?.userAgentData,
) => {
  if (!userAgentData?.getHighEntropyValues) return null;
  try {
    const { platform, platformVersion } =
      await userAgentData.getHighEntropyValues(['platformVersion']);
    const major = parseInt(platformVersion, 10);
    if (Number.isNaN(major)) return null;
    // Windows: platformVersion 13+ is Windows 11, 1-12 is Windows 10.
    if (platform === 'Windows')
      return major >= 13 ? '11' : major > 0 ? '10' : null;
    if (platform === 'macOS')
      return platformVersion.split('.').slice(0, 2).join('.');
    return null;
  } catch {
    return null;
  }
};
